import { useEffect } from "react";
import { useSafeLiveQuery } from "@/indexedDB/hooks/useSafeLiveQuery";
import {
  getMessageByClientIdFromLocalDB,
  getMessageByServerIdFromLocalDB,
} from "@/indexedDB/services/messages";

/**
 * Closes the attachment viewer when the message it is previewing gets deleted —
 * e.g. the sender cancelled a chunked upload we were watching-while-uploading
 * (or previewed their own cancelled upload). Closing unmounts the <video>,
 * which cancels the service-worker stream cleanly instead of surfacing a load
 * error.
 *
 * Returns whether the previewed message still exists so the caller can also
 * suppress a load-error flash in the window before the close lands:
 *   `undefined` = still loading · `true` = present · `false` = gone.
 */
export function useCloseViewerOnMessageDeleted(
  messageId: string | undefined,
  open: boolean,
  closeDialog: () => void,
): boolean | undefined {
  const messageExists = useSafeLiveQuery(
    () =>
      messageId
        ? // The captured messageId can be either the server id OR the optimistic
          // _clientId: a viewer opened on a just-sent (PENDING) message holds
          // the _clientId, and the server confirm rewrites the row's `id` IN
          // PLACE (the primary key `_clientId` never changes) — an id-only
          // lookup then misses and the viewer would self-close mid-view.
          // Checking both keys keeps it open across the rewrite, while a
          // genuinely deleted row (e.g. a cancelled upload) misses on both and
          // still closes.
          Promise.all([
            getMessageByServerIdFromLocalDB(messageId),
            getMessageByClientIdFromLocalDB(messageId),
          ]).then(
            ([byServerId, byClientId]) =>
              byServerId != null || byClientId != null,
          )
        : Promise.resolve(true),
    [messageId],
  );

  useEffect(() => {
    if (open && messageExists === false) {
      closeDialog();
    }
  }, [open, messageExists, closeDialog]);

  return messageExists;
}
